import React, { useMemo, useState, useEffect, useRef } from 'react'
import GradingState from '@/components/organisms/Grading/GradingState'
import { calculateTextAccuracy } from './utils/textAccuracy'

interface GradingEngWritngProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  responseJson?: string
  manualGradingResult?: 'correct' | 'incorrect' | 'grading'
  onManualGradingChange?: (status: 'correct' | 'incorrect' | undefined) => void
  onAccuracyChange?: (activityId: string, accuracy: number | null) => void
}

const GradingEngWritng = ({ type = 'scoreCard', activity, responseJson, manualGradingResult, onManualGradingChange, onAccuracyChange }: GradingEngWritngProps) => {
  const parsed = useMemo(() => {
    // configJson 파싱
    let config: any = {};
    try {
      if (activity?.configJson) {
        config = typeof activity.configJson === 'string' ? JSON.parse(activity.configJson) : activity.configJson;
      }
    } catch (e) {
      console.error('EngWriting configJson 파싱 실패:', e);
    }

    // responseJson 파싱
    let response: any = {};
    try {
      if (responseJson) response = JSON.parse(responseJson);
    } catch (e) {
      console.error('EngWriting responseJson 파싱 실패:', e);
    }

    // 실제 데이터 추출
    const correctSentence: string = config.correctSentence || config.fullText || '';
    const translation: string = config.translation || config.korean || '';
    const studentAnswer: string = response.answer || response.inputText || response.userInput || '';
    const isCorrectFlag: boolean | undefined = typeof response.isCorrect === 'boolean' ? response.isCorrect : undefined;

    // 전체 상태 계산
    const overallStatus: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading' =
      studentAnswer 
        ? (isCorrectFlag !== undefined 
            ? (isCorrectFlag ? 'correct' : 'word_incorrect') 
            : 'grading')
        : 'grading';

    return { 
      correctSentence, 
      studentAnswer, 
      overallStatus,
      translation,
      isCorrectFlag
    };
  }, [activity, responseJson]);

  const [manualStatus, setManualStatus] = useState<'correct' | 'incorrect' | null>(null);
  const autoAppliedRef = useRef(false);
  const activityKey = `${activity?.id ?? ''}-${responseJson ?? ''}`;
  const accuracyKey = activity?.id != null ? String(activity.id) : null;

  const hasManualResult = manualGradingResult === 'correct' || manualGradingResult === 'incorrect';

  useEffect(() => {
    autoAppliedRef.current = false;
    if (!hasManualResult) {
      setManualStatus(null);
    }
  }, [activityKey, hasManualResult]);

  // 저장된 채점 결과가 변경되면 상태 업데이트
  useEffect(() => {
    if (hasManualResult) {
      setManualStatus(manualGradingResult as 'correct' | 'incorrect');
    } else {
      setManualStatus(null);
    }
  }, [manualGradingResult, hasManualResult]);

  const accuracyPercent = useMemo(
    () => calculateTextAccuracy(parsed.correctSentence, parsed.studentAnswer),
    [parsed.correctSentence, parsed.studentAnswer],
  );

  useEffect(() => {
    if (!onAccuracyChange || !accuracyKey) return;
    onAccuracyChange(accuracyKey, accuracyPercent);
  }, [accuracyKey, accuracyPercent, onAccuracyChange]);

  useEffect(() => {
    if (!onAccuracyChange || !accuracyKey) return;
    return () => {
      onAccuracyChange(accuracyKey, null);
    };
  }, [accuracyKey, onAccuracyChange]);

  const hasStudentAnswer = (parsed.studentAnswer || '').trim().length > 0;
  const isExactMatch = useMemo(() => {
    if (!hasStudentAnswer) return false;
    if (!parsed.correctSentence) return false;
    return parsed.studentAnswer === parsed.correctSentence;
  }, [hasStudentAnswer, parsed.correctSentence, parsed.studentAnswer]);
  const autoWritingStatus: 'correct' | 'word_incorrect' = (() => {
    if (!hasStudentAnswer) return 'word_incorrect';
    if (parsed.isCorrectFlag !== undefined) return parsed.isCorrectFlag ? 'correct' : 'word_incorrect';
    return isExactMatch ? 'correct' : 'word_incorrect';
  })();

  useEffect(() => {
    if (type !== 'gradingCard') return;
    if (autoAppliedRef.current) return;
    if (hasManualResult) return;
    const normalized = autoWritingStatus === 'correct' ? 'correct' : 'incorrect';
    setManualStatus(normalized);
    onManualGradingChange?.(normalized);
    autoAppliedRef.current = true;
  }, [autoWritingStatus, type, hasManualResult, onManualGradingChange]);

  const displayStatus: 'correct' | 'word_incorrect' = 
    manualGradingResult && hasManualResult
      ? (manualGradingResult === 'correct' ? 'correct' : 'word_incorrect')
      : (manualStatus ? (manualStatus === 'correct' ? 'correct' : 'word_incorrect') : autoWritingStatus);
  
  const renderAlignedSentence = () => {
    const correct = parsed.correctSentence || '';
    const student = parsed.studentAnswer || '';
    if (!correct && !student) {
      return <span className='text-gray-500'>학생 입력이 없습니다.</span>;
    }

    // 단어 기준 토큰화 (공백 유지)
    const splitWithSpaces = (s: string) => s.split(/(\s+)/);
    const correctTokens = splitWithSpaces(correct);
    const studentTokens = splitWithSpaces(student);

    // 단어 비교를 위한 정규화 (대소문자/구두점 무시, 공백 제외)
    const normalize = (t: string) => t.replace(/[^a-z0-9']+/gi, '').toLowerCase();

    // studentTokens를 단어 위치 기준으로 매핑
    let sWordIdx = 0;
    const sWords = studentTokens.filter(tok => !/^\s+$/.test(tok));

    // 렌더는 correct의 토큰 흐름을 기준으로 진행 (원문 구조 유지)
    return (
      <span className='text-2xl font-bold'>
        {correctTokens.map((ct, i) => {
          if (/^\s+$/.test(ct)) {
            // 공백은 그대로
            return <span key={`sp-${i}`}>{ct}</span>;
          }

          // 현재 correct 단어에 대응하는 student 단어
          const cNorm = normalize(ct);
          const sTok = sWords[sWordIdx] ?? '';
          const sNorm = normalize(sTok);

          // 표시용 student 단어 (없으면 빈칸)
          const display = sTok || '___';

          // 정오답 판단
          const isEmpty = display === '___';
          const isCorrect = !isEmpty && cNorm === sNorm;

          // 다음 단어 인덱스 진전
          if (sTok) sWordIdx += 1;

          return (
            <span
              key={`w-${i}`}
              className={
                isEmpty
                  ? 'text-gray-400'
                  : isCorrect
                    ? 'text-green-600 font-semibold'
                    : 'text-red-600 underline'
              }
            >
              {display}
            </span>
          );
        })}
        {/* student가 더 길 경우 남은 부분 표시 (오류 방지) */}
        {sWords.slice(sWordIdx).map((extra, idx) => (
          <span key={`extra-${idx}`} className='text-red-600 underline'> {extra}</span>
        ))}
      </span>
    );
  };

  return (
    <div>
        <div className='flex flex-col gap-2'>
            <p className='text-lg font-bold'>{activity?.title || '영작'}</p>

            {parsed.translation && (
              <div className='p-4 rounded-2xl bg-gray-50 border border-gray-100'>
                <p className='text-xs font-semibold text-gray-500'>해석</p>
                <p className='text-sm text-gray-800 mt-1 whitespace-pre-line'>{parsed.translation}</p>
              </div>
            )}

            {/* 학생 입력 */}
            <div className={`overflow-hidden rounded-2xl border ${displayStatus === 'correct' ? 'border-green-500' : displayStatus === 'word_incorrect' ? 'border-red-500' : 'border-gray-200'}`}>
              <div className='flex justify-between items-center px-4 py-2 w-full'>
                <p className='text-sm font-bold text-gray-500'>학생 입력</p>
                <div className='flex items-center gap-3'>
                  <GradingState status={displayStatus} />
                </div>
              </div>
              <div className='flex justify-center items-center px-5 py-3 bg-gray-50'>
                {renderAlignedSentence()}
              </div>
            </div>

            {accuracyPercent !== null && (
              <div className='mt-4'>
                <div className='p-4 bg-gray-50 rounded-2xl border border-gray-100'>
                  <p className='text-sm text-gray-500'>영작 정답 근접도</p>
                  <p className='text-2xl font-bold text-indigo-600'>{accuracyPercent}%</p>
                </div>
              </div>
            )}
        </div>
    </div>
  )
}

export default GradingEngWritng